import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SitesService } from 'src/app/sites/sites.service';
import { DeviceFactory } from '../../../device-factory/device-factory';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { Subscription } from 'rxjs';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';

@Component({
	selector: 'app-battery-exception-widget',
	templateUrl: './battery-exception-widget.component.html',
	styleUrls: ['./battery-exception-widget.component.css']
})
export class BatteryExceptionWidgetComponent {
	@Input() widgetConfig: any;
	@Input() batteries: any[] = [];
	@Input() data: any[] = [];
	@Input() filters: {
		selectedBatteryVoltage: string;
		selectedBatteryCapacity: string;
		selectedBatteryManufacturer: string;
		selectedPeriod: string;
		selectedBatteryVintage: string[];
	} = null;

	isShownMap: boolean = false;
	siteId: number = 0;
	customerId: number = 0;

	deviceColor = DeviceFactory.createDevice(DeviceFactory.types.battlink).config.mapColor;

	// table data
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 20,
		draggable: true,
		fileName: '',
		csvFileHeaderInfo: []
	};

	columnConfig: ColumnsConfig[] = [];
	locations: any[] = [];
	siteSub: Subscription = new Subscription();
	siteInfo: any = {};

	constructor(
		public siteService: SitesService,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private commonService: CommonService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.tableConfig.fileName = this.widgetConfig.title;
		this.siteId = this.route.parent.snapshot.params.siteId || 0;
		this.customerId = this.route.parent.snapshot.params.customerId || 0;
		this.siteSub = this.sideMenuService.currentSite.subscribe((site) => {
			this.siteInfo = site;
			this.prepareColumnsNames();
		});
	}

	ngOnChanges() {
		this.updateChart();
	}

	prepareColumnsNames() {
		// common columns
		this.columnConfig = [
			{key: 'battery_id', name: this.translateService.instant('device_performance.battery_id'), type: "link"},
			{key: 'battery_sn', name: this.translateService.instant('device_performance.battery_sn'), type: "string"},
			{key: 'battery_manufacturer', name: this.translateService.instant('exception_reports.battery_manufacturer'), type: "string"},
			{key: 'battery_voltage', name: this.translateService.instant('exception_reports.battery_voltage'), type: "number"},
			{key: 'battery_capacity', name: this.translateService.instant('devices.battery_capacity'), type: "number"},
		];

		if (this.widgetConfig.temperature_batteries_table) {
			this.columnConfig = this.columnConfig.slice(0, 2);
		}

		// extra columns
		const extraFieldConfigs = {
			utilization_score: { type: 'number' },
			battery_energy_usage_score: { type: 'number' },
			ah_return_score: { type: 'number' },
			last_battery_location_long: { type: 'number' },
			last_battery_location_lat: { type: 'number' },
			days_outside_geofence: { type: 'number', noValueString: this.translateService.instant('g.n/a') },
			show_map_icon: { type: 'icon' },
			low_water_level_days: { type: 'number' },
			battery_temperature: { type: 'number' },
			missed_eq_opportunities: { type: 'number' },
			low_voltage_charge_cycles: { type: 'number', hasTooltip: this.translateService.instant('exception_reports.low_voltage_charge_cycles_tooltip') },
			low_voltage_discharge_cycles: { type: 'number', hasTooltip: this.translateService.instant('exception_reports.low_voltage_discharge_cycles_tooltip') },
			number_of_in_use_cycles: { type: 'number', hasTooltip: this.translateService.instant('exception_reports.number_of_in_use_cycles_tooltip') },
			number_of_not_plugged_in_active_days: { type: 'number', hasTooltip: this.translateService.instant('exception_reports.number_of_not_plugged_in_active_days_tooltip') },
			high_temperature_days: { type: 'number' },
			low_temperature_days: { type: 'number' },
		};

		if (!this.widgetConfig?.extra_fields?.length)
			return;

		this.widgetConfig.extra_fields.forEach((key) => {
			const config = extraFieldConfigs[key];
			if (config) {
				this.columnConfig.push({
					...config,
					key,
					name: this.translateService.instant(`exception_reports.${key}`),
				});
			}
		});
	}

	updateChart() {
		const chartData = this.data[this.filters.selectedPeriod]

		this.tableData = this.batteries.filter((battery) => {
			const batteryData = battery?.mac_address ? chartData?.[battery.mac_address] || {} : {};
			if (this.widgetConfig.has_voltage_filter && this.filters.selectedBatteryVoltage !== 'all' && battery.battery_voltage !== this.filters.selectedBatteryVoltage)
				return false;

			if (this.widgetConfig.has_capacity_filter && this.filters.selectedBatteryCapacity !== 'all' && battery.battery_capacity !== this.filters.selectedBatteryCapacity)
				return false;

			if (this.widgetConfig.has_manufacturer_filter && this.filters.selectedBatteryManufacturer !== 'all' && battery.battery_manufacturer !== this.filters.selectedBatteryManufacturer)
				return false;

			if (this.widgetConfig.has_vintage_filter && !this.siteDashboardService.isVintageWithinFilter(battery, this.filters.selectedBatteryVintage))
				return false;

			let showDevice = batteryData?.showDevice == true;
			if (this.widgetConfig?.extra_fields?.includes('last_battery_location_long')) {
				showDevice = battery.is_far_device == true;
				if (battery.has_location)
					this.addLocation(battery);
			}

			return showDevice;
		}).map((battery) => {
			const data = {};
			const batteryData = battery?.mac_address ? chartData?.[battery.mac_address] || {} : {};

			for(const column of this.columnConfig) {
				data[column.key] = this.formatTableData(column.key, battery, batteryData);
			}
			return data;
		});
		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			includeSpacing: false
		});
	}

	addLocation(device: any) {
		this.locations.push({
			latitude: device.last_battery_location_lat,
			longitude: device.last_battery_location_long,
			type: 'device',
			truck_id: device.battery_id,
			truck_sn: device.battery_sn,
			mac_address: device.mac_address,
			site_id: this.siteId,
			customer_id: this.customerId,
			serial_number: device.serial_number,
			sct_device_type: DeviceFactory.types.battlink,
			color: this.deviceColor,
			group: true,
		});
	}

	formatTableData(key: any, battery, batteryData) {
		const res = {
			value: battery[key] || 0,
			link:  ['/', this.customerId, this.siteId, battery.mac_address],
		};

		switch (key) {
			case 'utilization_score':
			case 'battery_energy_usage_score':
			case 'ah_return_score':
			case 'low_water_level_days':
			case 'missed_eq_opportunities':
			case 'low_voltage_charge_cycles':
			case 'low_voltage_discharge_cycles':
			case 'battery_temperature':
			case 'number_of_in_use_cycles':
			case 'number_of_not_plugged_in_active_days':
			case 'low_temperature_days':
			case 'high_temperature_days':
				res.value = batteryData?.value || 0;
				break;
			case 'days_outside_geofence':
				res.value = battery[key] ? Math.round(battery[key]/ (60*60*24)) : 0
				if (battery.is_estimated_location)
					res.value = null; // an estimated location has no GPS fix age - the column shows N/A (ST-2689)
				break;
			default:
				break;
		}

		return res;
	}

	ngOnDestroy() {
		if (this.siteSub)
			this.siteSub.unsubscribe();
	}
}
